import AppService from './AppService';

/**
 * Service for handling account management related API calls
 */
class AccountService {
  /**
   * Get account overview information for the current user/organization
   * @returns {Promise} Promise object with account data
   */
  static getAccountOverview() {
    return AppService.get('/api/accounts/my-overview');
  }

  /**
   * Get list of invoices for the current user/organization
   * @param {Object} params Optional query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with invoice list data
   */
  static getInvoices(params = {}) {
    return AppService.get('/api/accounts/invoices', { params });
  }

  /**
   * Get specific invoice details
   * @param {string} invoiceId Invoice ID to fetch
   * @returns {Promise} Promise object with invoice details
   */
  static getInvoiceDetails(invoiceId) {
    return AppService.get(`/api/accounts/invoices/${invoiceId}`);
  }

  /**
   * Get list of statements for the current user/organization
   * @param {Object} params Optional query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with statement list data
   */
  static getStatements(params = {}) {
    return AppService.get('/api/accounts/statements', { params });
  }

  /**
   * Get specific statement details
   * @param {string} statementId Statement ID to fetch
   * @returns {Promise} Promise object with statement details
   */
  static getStatementDetails(statementId) {
    return AppService.get(`/api/accounts/statements/${statementId}`);
  }

  /**
   * Make a payment for an invoice
   * @param {Object} paymentData Payment data (amount, invoiceId, paymentMethod, etc.)
   * @returns {Promise} Promise object with payment confirmation
   */
  static makePayment(paymentData) {
    return AppService.post('/api/accounts/payments', paymentData);
  }

  /**
   * Get list of completed payments for a specific account
   * @param {string} accountId Account ID to fetch payments for
   * @param {Object} params Optional query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with payment list data
   */
  static getAccountPayments(accountId, params = {}) {
    return AppService.get(`/api/accounts/${accountId}/payments`, { params });
  }

  /**
   * Get list of pending payments for a specific account
   * @param {string} accountId Account ID to fetch pending payments for
   * @param {Object} params Optional query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with pending payment list data
   */
  static getAccountPendingPayments(accountId, params = {}) {
    return AppService.get(`/api/accounts/${accountId}/pending-payments`, { params });
  }

  /**
   * Get list of related tickets for a specific account
   * @param {string} accountId Account ID to fetch related tickets for
   * @param {Object} params Optional query parameters (page, size, sort, etc.)
   * @returns {Promise} Promise object with related tickets list data
   */
  static getAccountRelatedTickets(accountId, params = {}) {
    return AppService.get(`/api/accounts/${accountId}/related-tickets`, { params });
  }

  /**
   * Get account transaction history (all financial transactions)
   * @param {string} accountId Account ID to fetch transactions for
   * @param {Object} params Optional query parameters (page, size, sort, date range, etc.)
   * @returns {Promise} Promise object with transaction history data
   */
  static getAccountTransactions(accountId, params = {}) {
    return AppService.get(`/api/accounts/${accountId}/transactions`, { params });
  }

  /**
   * Get wallet information for the current user/organization
   * @param {string} accountId Account ID to fetch wallet for
   * @returns {Promise} Promise object with wallet data
   */
  static getWalletInfo(accountId) {
    return AppService.get(`/api/accounts/${accountId}/wallet`);
  }

  /**
   * Update account payment methods
   * @param {string} accountId Account ID to update payment methods for
   * @param {Object} paymentMethodData Payment method data
   * @returns {Promise} Promise object with updated payment methods
   */
  static updatePaymentMethods(accountId, paymentMethodData) {
    return AppService.put(`/api/accounts/${accountId}/payment-methods`, paymentMethodData);
  }
}

export default AccountService;
